import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Intervention } from "../../types";



interface CreateInterventionRequest {
  methodology_id: string;
  name: string;
  description: string;
}

interface CreateInterventionResponse {
  success: string;
  data: Intervention | Intervention[];
}

interface CreateInterventionsBulkResponse {
  success: string;
  data: Intervention[];
}

interface UpdateInterventionRequest {
  id: string;
  body: Partial<Intervention>;
}

export const interventionApi = createApi({
  reducerPath: "interventionDefinitionApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/is/intervention",
    prepareHeaders: (headers, { getState }) => {
      // Type assertion for the auth slice
      const state = getState() as any;
      const token = state.auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Intervention"],
  endpoints: (build) => ({
    addIntervention: build.mutation<CreateInterventionResponse, { projectId: string; interventions: CreateInterventionRequest | CreateInterventionRequest[] }>({
      query: ({ projectId, interventions }) => ({
        url: `?project_id=${projectId}`,
        method: "POST",
        body: interventions,
      }),
      invalidatesTags: ["Intervention"],
    }),

    addInterventionsBulk: build.mutation<CreateInterventionsBulkResponse, CreateInterventionRequest[]>({
      query: (interventions) => ({
        url: "/bulk",
        method: "POST",
        body: interventions,
      }),
      invalidatesTags: ["Intervention"],
    }),

    getIntervention: build.query<Intervention, string>({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: "Intervention", id }],
    }),

    updateIntervention: build.mutation<Intervention, UpdateInterventionRequest>(
      {
        query: ({ id, body }) => ({
          url: `/${id}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: (result, error, { id }) => [
          { type: "Intervention", id },
        ],
      }
    ),

    deleteIntervention: build.mutation<
      { success: boolean; id: string },
      string
    >({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Intervention", id }],
    }),

    getInterventionByProject: build.query<Intervention[], string>({
      query: (projectId) => `/project/${projectId}`,
      transformResponse: (response: {
        data: Intervention[];
        success: string;
      }) => {
        // Extract the data array from the response
        return response.data || [];
      },
      providesTags: (result) =>
        {
          console.log("[ getInterventionByProject ] result", result);
          return result
          ? [
              ...result.map(({ intervention_id }) => ({
                type: "Intervention" as const,
                id: intervention_id,
              })),
              { type: "Intervention", id: "LIST" },
            ]
          : [{ type: "Intervention", id: "LIST" }]
        },
    }),
  }),
});

export const {
  useAddInterventionMutation,
  useGetInterventionQuery,
  useUpdateInterventionMutation,
  useDeleteInterventionMutation,
  useGetInterventionByProjectQuery,
  useLazyGetInterventionQuery,
  useLazyGetInterventionByProjectQuery,
} = interventionApi;
